import Link from 'next/link'
import {useTranslation} from 'next-i18next'
import React, {useRef, useState} from 'react'
import styled from 'styled-components'
import {Document} from '@contentful/rich-text-types'
import {Box, CallToAction, Card, Container, Section} from '@miro-site/core'
import {
  CardGrid,
  Category,
  CategoryGroup,
  RichText,
  TemplateCard,
  TemplateCardProps,
} from '@miro-site/templates-design'
import {SelectedTemplate, TemplateLoader} from '@mirohq-internal/website-ui-foundation'
import {TSS} from '../data/api/tss'
import {TemplateCardData} from '../data/mappers/toTemplateCard'
import TemplatesSearch from './TemplatesSearch'

export interface CategoryPageProps {
  slug: string
  title: string
  description: string
  templates: TemplateCardData[]
  categoryGroups: CategoryGroup[]
  seoContentBlock?: Document
}

const SeoContentSection = styled(Section).attrs({as: 'section'})`
  p:last-child {
    margin-bottom: 0;
  }
  ${Card} {
    box-shadow: none;
    background-color: var(--wcds-templates-cta-background-color);
    border: 1px solid var(--wcds-templates-cta-border-color);
  }
`

const CategoryPage: React.FC<CategoryPageProps> = ({
  title,
  description,
  templates = [],
  categoryGroups = [],
  seoContentBlock,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<SelectedTemplate>({
    metadataId: null,
  })

  const headerRef = useRef<HTMLElement>(null)

  const {t} = useTranslation('common')
  const getActionButtonProps = ({
    entryId,
    metadataId,
    newBoardMetadataId,
    accessConstraints,
  }: TemplateCardData): TemplateCardProps['actionButtonProps'] => {
    if (accessConstraints?.requiredPlans?.length > 0) {
      return {
        children: t('templates-actions-open-template'),
        onClick: e => {
          e.preventDefault()
          setSelectedTemplate({entryId, metadataId})
        },
      }
    }

    return {
      onClick: e => {
        e.preventDefault()
        setSelectedTemplate({entryId, metadataId: newBoardMetadataId})
      },
    }
  }

  const handleOnTemplateCopy = (copiedTemplateEntryId: string) => {
    TSS.copy(copiedTemplateEntryId)
  }

  return (
    <>
      <main data-selected-template-id={selectedTemplate.metadataId}>
        <Box $mt={30} $mb={60}>
          <TemplatesSearch
            withBackButton
            categoryGroups={categoryGroups}
            stickyFixingPoint={headerRef}
          />
        </Box>
        <Container ref={headerRef}>
          <Category.Title>{title}</Category.Title>
          <Category.Description>{description}</Category.Description>
        </Container>
        {templates.length > 0 && (
          <CardGrid $pt={20}>
            {templates.map((tpl, i) => (
              <TemplateCard
                templateActions={{
                  PREVIEW: t('templates-actions-preview'),
                  USE_TEMPLATE: t('templates-actions-use-template'),
                  SHOW_MORE_INFO: t('templates-actions-more-info'),
                }}
                key={tpl.templatePagePath}
                previewButton={defaultButton => (
                  <Link href={tpl.templatePagePath} passHref legacyBehavior>
                    {defaultButton}
                  </Link>
                )}
                actionButtonProps={getActionButtonProps(tpl)}
                position={i}
                {...tpl}
              />
            ))}
          </CardGrid>
        )}
        <CallToAction
          background="var(--wcds-templates-cta-background-color)"
          border="1px solid var(--wcds-templates-cta-border-color)"
          heading={t('signup-cta-headline')}
          content={t('signup-cta-description')}
          button={{
            children: t('signup-cta-button'),
            href: '/signup',
          }}
        />

        {seoContentBlock ? (
          <SeoContentSection as="section" type="large">
            <Container>
              <Card $p={[20, 40]} $pr={[20, 120]}>
                <RichText document={seoContentBlock} />
              </Card>
            </Container>
          </SeoContentSection>
        ) : null}
      </main>
      <TemplateLoader
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        onTemplateCopy={handleOnTemplateCopy}
      />
    </>
  )
}

export default CategoryPage
